import { Message, Repo } from '../types'
import { HeroVariantsGrid } from './HeroVariants'
import { InfoIcon } from 'lucide-react'
import Image from 'next/image'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import RepositorySelect from './RepositorySelect'
import ReactMarkdown from 'react-markdown'
import SkipRepoSelection from './SkipRepoSelectino'
import { useIsMobile } from '@/hooks/use-mobile'
import { useState } from 'react'

interface ImageType {
  url: string;
  actionButton?: {
    text: string;
    onClick: () => void;
  };
}

interface Choice {
  id: number;
  text: string;
  tooltip?: string;
  description?: string;
}

interface ChatMessageProps {
  message: Message
  heroVariants?: Array<{
    id: number;
    description: string;
    html: string;
  }> | null;
  onVariantSelect?: (variant: { id: number; description: string; html: string }) => void;
  selectedVariantId?: number;
  onChoiceSelect?: (choice: { id: number; text: string }) => void;
  isDefaultFlow?: boolean;
  repos?: Repo[];
  onRepoSelect?: (repo: Repo) => void;
  onRepoIgnore?: () => void;
  selectedRepoId?: string;
  websiteType: string
  isTyping: boolean
  isRepoSelected: boolean;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  heroVariants,
  onVariantSelect,
  selectedVariantId,
  onChoiceSelect,
  isDefaultFlow,
  repos,
  onRepoSelect,
  onRepoIgnore,
  selectedRepoId,
  websiteType,
  isTyping,
  isRepoSelected
}) => {
  const isMobile = useIsMobile();
  const [openTooltips, setOpenTooltips] = useState<Record<string, boolean>>({});
  
  const toggleTooltip = (id: number) => {
    setOpenTooltips(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  return (
    <div
      className={`mb-4 flex items-start gap-2 ${
        message.sender === 'user' ? 'flex-row-reverse' : 'flex-row'
      }`}
    >
      <div className={`flex-shrink-0 rounded-full p-1.5 flex items-center justify-center ${
        message.sender === 'user' ? 'bg-[#EAF0FF]' : isDefaultFlow ? 'bg-[#1E1E1E]' : 'bg-[#2E6CFF]'
      }`}>
        <Image 
          src={message.sender === 'user' ? '/images/user.svg' : isDefaultFlow ? '/images/orange-AI.svg' : '/images/AI.svg'} 
          alt={message.sender === 'user' ? 'User' : 'AI'}
          width={25}
          height={25}
          className='flex-shrink-0'
        />
      </div>
      <div
        className={`max-w-full rounded-lg px-4 py-2 ${
          message.sender === 'user'
            ? isDefaultFlow ? 'bg-[#1E1E1E] text-white' : 'bg-[#2E6CFF] text-white'
            : isDefaultFlow ? 'bg-[#1E1E1E] text-white' : 'bg-[#F3F4F6] text-gray-900'
        }`}
      >
        <ReactMarkdown>{message.text}</ReactMarkdown>

        {message.sender === 'bot' && message.choices && message.choices.length > 0 && (
          <div className="flex flex-col gap-2">
            {message.choices.map((choice) => (
              <div key={choice.id} className="relative">
              <div className="flex items-center w-full">
                <button
                  disabled={([1, 2, 3].includes(choice.id) && !!websiteType || isTyping)}
                  onClick={() => onChoiceSelect?.(choice)}
                  className={`w-full h-[40px] py-2 px-4 text-left rounded-lg relative flex items-center justify-between group shadow-sm hover:shadow-md ${([1, 2, 3].includes(choice.id) && !!websiteType || isTyping) ? 'cursor-not-allowed' : 'cursor-pointer'} ${isDefaultFlow ? 'bg-[#2D3239] text-white' : 'bg-white'}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`text-[16px] mx-2 font-semibold ${isDefaultFlow ? 'text-white' : 'text-gray-900'}`}>
                      {choice.text}
                    </span>
                    {choice.tooltip && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-1 px-1 rounded-[8px] border border-[#FE6434] transition-colors">
                              <Image 
                                src="/images/CreditsIconFill.svg" 
                                alt="Credits Image" 
                                width={15} 
                                height={15} 
                                className='w-[15px] h-[15px] object-cover'
                              />
                              <span className='text-[14px] font-medium text-[#FE6434]'>
                                {choice.tooltip.split(':')[1].trim().split(' ')[0]}
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent
                            side='bottom'
                            align='center'
                            className='rounded-lg bg-[#1E1E1E] px-4 py-2 text-sm text-white'
                          >
                            <div className='whitespace-nowrap text-center'>
                              <p className='font-medium'>{choice.text}</p>
                              <p>{choice.tooltip}</p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </button>
                
                {choice.description && (
                  <div className="ml-2">
                    <TooltipProvider>
                      <Tooltip open={isMobile ? openTooltips[choice.id] : undefined}>
                        <TooltipTrigger asChild>
                          <div 
                            className='cursor-pointer'
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              if (isMobile) {
                                e.preventDefault();
                                toggleTooltip(choice.id);
                              }
                            }}
                          >
                            <InfoIcon className='h-4 w-4 text-gray-400' />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent
                          side={isMobile ? 'bottom' : 'right'}
                          align={isMobile ? 'center' : 'start'}
                          className='max-w-[300px] rounded-md border border-gray-200 bg-white p-2 text-sm text-gray-500 shadow-lg'
                          sideOffset={5}
                          onPointerDownOutside={() => {
                            if (isMobile && openTooltips[choice.id]) {
                              toggleTooltip(choice.id);
                            }
                          }}
                        >
                          <p>{choice.description || choice.tooltip}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}
              </div>
            </div>
            ))}
          </div>
        )}

        {message?.images && message?.images.length > 0 && (
          <div className='mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3'>
            {message.images.map((image, index) => (
              <div key={index} className='group relative'>
                <div className='relative aspect-square w-full'>
                  <Image
                    src={image.url}
                    alt={`Generated image ${index + 1}`}
                    fill
                    className='rounded-lg object-cover'
                  />
                  {image.actionButton && (
                    <div className='absolute inset-0 flex items-center justify-center rounded-lg bg-black/20 opacity-0 transition-all duration-200 group-hover:opacity-100'>
                      <button
                        onClick={image.actionButton.onClick}
                        className='flex h-[44px] w-[65px] items-center justify-center gap-[10px] rounded-[8px] bg-white p-[10px] text-[#2E6CFF] shadow-md transition-colors hover:bg-gray-100'
                      >
                        {image.actionButton.text}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {message.sender === 'bot' &&
          message?.repos &&
          message?.repos?.length > 0 && (
            <div className='mt-5 grid grid-cols-2 gap-2'>
              {repos?.map((repo) => (
                <RepositorySelect
                  key={repo.id}
                  repo={repo}
                  colors={repo?.colors}
                  onSelect={onRepoSelect!}
                  isSelected={repo.id === selectedRepoId}
                  disabled={!!selectedRepoId || isRepoSelected || isTyping}
                />
              ))}
              <SkipRepoSelection
                onSkip={onRepoIgnore!}
                label='Skip Repo Selection'
                disabled={!!selectedRepoId || isRepoSelected || isTyping}
              />
            </div>
          )}

        {message.sender === 'bot' && heroVariants && (
          <div className="mt-4">
            <HeroVariantsGrid 
              variants={message.showVariants ? heroVariants : null} 
              onSelect={onVariantSelect!}
              selectedId={selectedVariantId}
            />
          </div>
        )}
      </div>
    </div>
  )
}
